import { useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../keys'
import { ApiError, getAccessToken } from '../client'
import type { FavoriteLocationResponse } from '../queries/favorite-locations'
import { API_BASE_URL } from '@/lib/config'

export interface FavoriteLocationCreate {
  name: string
  latitude: number
  longitude: number
  radius?: number
  filters?: Record<string, boolean> | null
  is_default?: boolean
}

export interface FavoriteLocationUpdate {
  name?: string
  latitude?: number
  longitude?: number
  radius?: number
  filters?: Record<string, boolean> | null
  is_default?: boolean
}

async function apiRequest<T>(
  path: string,
  method: string,
  body?: unknown,
): Promise<T> {
  const token = await getAccessToken()
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
  if (!response.ok) {
    const detail = await response
      .json()
      .then((b) => b.detail)
      .catch(() => response.statusText)
    throw new ApiError(response.status, detail)
  }
  if (response.status === 204) return undefined as T
  return response.json()
}

export function useCreateFavoriteLocation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (location: FavoriteLocationCreate) => {
      return apiRequest<FavoriteLocationResponse>(
        '/api/v1/favorite-locations',
        'POST',
        location,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.favoriteLocations.all,
      })
    },
  })
}

export function useUpdateFavoriteLocation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      locationId,
      updates,
    }: {
      locationId: number
      updates: FavoriteLocationUpdate
    }) => {
      return apiRequest<FavoriteLocationResponse>(
        `/api/v1/favorite-locations/${locationId}`,
        'PATCH',
        updates,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.favoriteLocations.all,
      })
    },
  })
}

export function useDeleteFavoriteLocation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (locationId: number) => {
      return apiRequest<void>(
        `/api/v1/favorite-locations/${locationId}`,
        'DELETE',
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.favoriteLocations.all,
      })
    },
  })
}
